/* The Open picker (app.ts renderPicker ~2151, renderDirTree ~2342,
   renderPickerTree ~2401): the file-explorer overlay of a section's records.
   Directory sections (Projects, Products/Platforms) draw the nested
   folder tree with an inline New-folder form (DIR4); the legacy single-view
   sections (Teams, Permissions) draw the flat view tree. Backdrop, Cancel, and
   Escape (wired globally in useHermesData) close it. The page behind is made
   `inert` while it is up (aria-modal's promise) — App applies that.

   React keeps the <input> element identity across re-renders, so the search
   box holds focus and caret without the vanilla shell's restoration dance. */
import { useRef, type CSSProperties, type JSX } from "react";
import { esc } from "../../esc";
import { directoryPath } from "../../../shared/directories";
import { useAppState } from "./store";
import { iPanel } from "./helpText";
import {
  currentSection,
  dirPickerRows,
  pickerTree,
  type DirRowItem,
  type Section,
  type SectionDir,
  type SectionView,
} from "./sections";

const depthStyle = (depth: number): CSSProperties => ({ ["--depth" as string]: String(depth) } as CSSProperties);

export function Picker(): JSX.Element | null {
  const { state, store } = useAppState();
  const section = currentSection(store);
  if (!state.pickerOpen || !section) return <div id="picker" hidden />;

  return (
    <div id="picker" role="dialog" aria-modal="true" aria-label={`View a ${section.label} record`}>
      <div className="picker-backdrop" onClick={() => store.closePicker()} />
      <div className="picker-panel">
        <div className="picker-title">View — {section.label}</div>
        <label className="picker-find">
          <span className="sr-only">Search {section.label}</span>
          <input
            className="picker-search"
            type="search"
            autoComplete="off"
            autoFocus
            placeholder={`Search ${section.label}…`}
            value={state.pickerSearch}
            onChange={(e) => store.setPickerSearch(e.target.value)}
          />
        </label>
        <div className="picker-tree">
          {section.dir ? (
            <DirTree sd={section.dir} />
          ) : (
            <LegacyTree section={section} />
          )}
        </div>
        <div className="picker-actions">
          {section.dir && (
            <button className="picker-new-folder btn ghost" onClick={() => store.openNewFolder()}>
              New folder…
            </button>
          )}
          <button className="picker-cancel btn ghost" onClick={() => store.closePicker()}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- the directory tree (designs/directories.md) ---- */

function DirTree({ sd }: { sd: SectionDir }): JSX.Element {
  const { state, store } = useAppState();
  const rows = state.dirsError || state.dirsPending ? [] : dirPickerRows(state, sd);
  return (
    <>
      {state.newFolderOpen && <NewFolderForm sd={sd} />}
      {state.dirsError ? (
        <p className="picker-empty dirs-error">
          Folders not recorded — couldn't load. Records may still be reachable by search once it
          recovers.
        </p>
      ) : state.dirsPending ? (
        <p className="picker-empty" aria-busy="true">
          Loading folders…
        </p>
      ) : rows.length ? (
        rows.map((row, i) => <DirRow key={i} row={row} sd={sd} />)
      ) : (
        <p className="picker-empty">Nothing matches “{state.pickerSearch.trim()}”.</p>
      )}
    </>
  );
}

function DirRow({ row, sd }: { row: DirRowItem; sd: SectionDir }): JSX.Element {
  const { state, store } = useAppState();
  if (row.kind === "record") {
    const current = row.rec.id === sd.openId() ? " current" : "";
    return (
      <button className={"file" + current} style={depthStyle(row.depth)} onClick={() => sd.open(row.rec.id)}>
        <span className="file-icon" aria-hidden="true" />
        <span className="file-name">{row.rec.name}</span>
        <span className={`state record-status status-${esc(row.rec.status)}`}>{row.rec.status}</span>
      </button>
    );
  }
  const name = row.kind === "dir" ? row.dir.name : "Not filed";
  const id = row.kind === "dir" ? row.dir.id : "";
  const selected = state.selectedDirId === (row.kind === "dir" ? row.dir.id : null) ? " selected" : "";
  const cls = row.kind === "dir" ? "folder dir-folder" : "folder dir-folder unfiled";
  return (
    <button
      className={cls + selected}
      aria-expanded={row.open}
      style={depthStyle(row.depth)}
      onClick={() => store.toggleDir(id)}
    >
      <span className="twisty" aria-hidden="true">
        {row.open ? "▾" : "▸"}
      </span>
      <span className="folder-icon" aria-hidden="true" />
      <span className="folder-name">{name}</span>
      <span className={"folder-count" + (state.loadError ? " missing" : "")}>
        {state.loadError
          ? "not recorded — couldn't load"
          : `${row.count} ${sd.noun}${row.count === 1 ? "" : "s"}`}
      </span>
    </button>
  );
}

function NewFolderForm({ sd }: { sd: SectionDir }): JSX.Element {
  const { state, store } = useAppState();
  const nameRef = useRef<HTMLInputElement>(null);
  const into = state.selectedDirId
    ? directoryPath(state.selectedDirId, state.directories)
    : "the top level";
  const create = (): void => void store.createFolder(sd.section, nameRef.current?.value ?? "");
  return (
    <div className="new-folder">
      {store.on("about-hermes") && (
        <div style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: iPanel(state.me.surface, "directories") }} />
      )}
      <label className="field">
        <span className="field-label">New folder in {into}</span>
        <input
          ref={nameRef}
          className="new-folder-name"
          type="text"
          placeholder="e.g. Mining"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") create();
          }}
        />
      </label>
      <p className="form-error new-folder-error" role="alert">
        {state.newFolderError ?? ""}
      </p>
      <div className="form-actions">
        <button className="new-folder-create btn primary" onClick={create}>
          Create folder
        </button>
        <button className="new-folder-cancel btn ghost" onClick={() => store.cancelNewFolder()}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---- the legacy view tree (Teams, Permissions) ---- */

function LegacyTree({ section }: { section: Section }): JSX.Element {
  const { state, store } = useAppState();
  const tree = pickerTree(state, section);
  if (!tree.length)
    return <p className="picker-empty">Nothing matches “{state.pickerSearch.trim()}”.</p>;
  return (
    <>
      {tree.map((node) => (
        <FolderNode key={node.view.label} view={node.view} files={node.files} open={node.open} />
      ))}
    </>
  );
}

function FolderNode({
  view,
  files,
  open,
}: {
  view: SectionView;
  files: { id: string; name: string }[] | null;
  open: boolean;
}): JSX.Element {
  const { store } = useAppState();
  const count = view.count!();
  const twisty = files ? (open ? "▾" : "▸") : "";
  return (
    <>
      <button
        className="folder current"
        aria-expanded={files ? open : undefined}
        onClick={() => store.toggleFolder(view.label, () => view.select!(), Boolean(files))}
      >
        <span className="twisty" aria-hidden="true">
          {twisty}
        </span>
        <span className="folder-icon" aria-hidden="true" />
        <span className="folder-name">{view.label}</span>
        <span className={"folder-count" + (count ? "" : " missing")}>
          {count ? `${count.n} ${count.noun}${count.n === 1 ? "" : "s"}` : "not recorded — couldn't load"}
        </span>
      </button>
      {open && files && (
        <div className="tree-children">
          {files.length ? (
            files.map((r) => (
              <button
                key={r.id}
                className={"file" + (r.id === view.openId!() ? " current" : "")}
                onClick={() => view.openFile!(r.id)}
              >
                <span className="file-icon" aria-hidden="true" />
                <span className="file-name">{r.name}</span>
              </button>
            ))
          ) : (
            <p className="picker-empty">Nothing in here yet.</p>
          )}
        </div>
      )}
    </>
  );
}
